/**
 * Editor content composable
 * Handles content retrieval, parsing, and synchronization for contenteditable editor
 */

import { ref } from 'vue';
import { normalizeColorToHex } from '../utils/material-utils';
import { nodeRegistry } from '../core/NodeRegistry';
import {
  parseSeedeTheme,
  parseSeedeStyle,
  parseVeesoReferenceImage,
  parseVeesoCanvasSize,
} from '../utils/prompt-parser';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Object} options.chatState - Chat state object
 * @param {Function} options.emit - Vue emit function
 * @param {Function} options.adjustHeight - Adjust height function
 * @returns {Object} Content handlers and state
 */
export function useEditorContent(options = {}) {
  const { editorRef, chatState, emit, adjustHeight, syncAssetsWithEditor } = options;

  // Store last synced value to avoid circular updates
  const lastSyncedValue = ref('');
  const isEmpty = ref(true);

  const toRegistryType = (dataType) => (dataType === 'userskill' ? 'skill' : dataType);

  const trimWrappingBackticks = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed.startsWith('`') && trimmed.endsWith('`') && trimmed.length >= 2) {
      return trimmed.slice(1, -1).trim();
    }
    return trimmed;
  };

  const hashString = (value) => {
    const str = String(value || '');
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
  };

  /**
   * Check if node is inside special element (image, tag, color)
   * @param {Node} node
   * @param {HTMLElement} root
   * @returns {boolean}
   */
  const isInsideSpecialElement = (node, root) => {
    let parent = node.parentNode;
    while (parent && parent !== root) {
      if (parent.nodeType === Node.ELEMENT_NODE) {
        const dataType = parent.getAttribute?.('data-type');
        if (
          dataType === 'image' ||
          dataType === 'tag' ||
          dataType === 'color' ||
          dataType === 'remix' ||
          dataType === 'userskill' ||
          dataType === 'document'
        ) {
          return true;
        }
      }
      parent = parent.parentNode;
    }
    return false;
  };

  /**
   * Check if editor is empty (fast version - avoids full text extraction)
   * @returns {boolean}
   */
  const checkEmpty = () => {
    if (!editorRef?.value) return true;
    const editor = editorRef.value;

    // Quick checks first (cheap DOM queries)
    if (editor.querySelector('[data-type="image"]')) return false;
    if (editor.querySelector('[data-type="tag"]')) return false;
    if (editor.querySelector('[data-type="color"]')) return false;
    if (editor.querySelector('[data-type="remix"]')) return false;
    if (editor.querySelector('[data-type="userskill"]')) return false;
    if (editor.querySelector('[data-type="document"]')) return false;

    // Check for text content (faster than getPlainText)
    const textContent = editor.textContent || '';
    return !textContent.trim();
  };

  /**
   * Get plain text content for v-model sync - optimized with TreeWalker
   * @returns {string}
   */
  const getPlainText = () => {
    if (!editorRef?.value) return '';

    const result = [];
    const root = editorRef.value;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Skip special element children (will process separately)
        if (node.nodeType === Node.ELEMENT_NODE) {
          const dataType = node.getAttribute?.('data-type');
          if (
            dataType === 'image' ||
            dataType === 'tag' ||
            dataType === 'color' ||
            dataType === 'remix' ||
            dataType === 'userskill' ||
            dataType === 'document'
          ) {
            return NodeFilter.FILTER_ACCEPT;
          }
          // Block element markers
          if (node.nodeName === 'BR') {
            return NodeFilter.FILTER_ACCEPT;
          }
          if (node.nodeName === 'DIV' || node.nodeName === 'P') {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        }
        // Text nodes: check if inside special element, skip if so
        if (node.nodeType === Node.TEXT_NODE) {
          if (isInsideSpecialElement(node, root)) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let prevWasBlock = false;
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        result.push(node.textContent);
        prevWasBlock = false;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const dataType = node.getAttribute?.('data-type');

        if (
          dataType === 'image' ||
          dataType === 'tag' ||
          dataType === 'color' ||
          dataType === 'remix' ||
          dataType === 'userskill' ||
          dataType === 'document'
        ) {
          result.push(nodeRegistry.serialize(toRegistryType(dataType), node, {}));
          prevWasBlock = false;
        } else if (node.nodeName === 'BR') {
          result.push('\n');
          prevWasBlock = false;
        } else if ((node.nodeName === 'DIV' || node.nodeName === 'P') && !prevWasBlock) {
          // Add newline at block element start (unless first or already has newline)
          if (result.length > 0 && !result[result.length - 1]?.endsWith('\n')) {
            result.push('\n');
          }
          prevWasBlock = true;
        }
      }
    }

    return result.join('');
  };

  /**
   * Get formatted content for submission
   * @returns {{ text: string, images: Array, tags: Array, colors: Array }}
   */
  const getFormattedContent = () => {
    if (!editorRef?.value) return { text: '', images: [], tags: [], colors: [] };

    const clone = editorRef.value.cloneNode(true);
    const images = [];
    const tags = [];
    const colors = [];
    const pendingUploads = [];

    // Collect image materials and replace with @SeedeMaterial format in text
    clone.querySelectorAll('[data-type="image"]').forEach((img) => {
      const loading = img.getAttribute('data-loading') === 'true';
      const uploadStatus = img.getAttribute('data-upload-status') || '';
      const progress = parseFloat(img.getAttribute('data-upload-progress') || '0') || 0;
      const assetId = img.getAttribute('data-asset-id');
      const url = img.getAttribute('data-url');
      const tag = img.getAttribute('data-tag') || '';
      const isMaterial = img.getAttribute('data-is-material') !== 'false';
      const width = parseInt(img.getAttribute('data-width') || '128');
      const height = parseInt(img.getAttribute('data-height') || '128');
      const filename = img.getAttribute('data-filename') || '';

      if (loading || uploadStatus === 'uploading' || uploadStatus === 'failed') {
        pendingUploads.push({
          id: assetId,
          type: 'image',
          status: uploadStatus || 'uploading',
          progress,
        });
        img.replaceWith('');
        return;
      }

      const finalAsset = {
        id: assetId,
        url,
        tag,
        isMaterial,
        width,
        height,
        name: filename,
      };
      images.push(finalAsset);
      img.replaceWith(nodeRegistry.serialize('image', img, {}));
    });

    // Collect tags
    clone.querySelectorAll('[data-type="tag"]').forEach((tagEl) => {
      tags.push({
        id: tagEl.getAttribute('data-tag-id'),
        label: tagEl.getAttribute('data-label'),
        value: tagEl.getAttribute('data-value'),
      });
      tagEl.replaceWith(nodeRegistry.serialize('tag', tagEl, {}));
    });

    // Collect colors
    clone.querySelectorAll('[data-type="color"]').forEach((colorEl) => {
      const colorValue = colorEl.getAttribute('data-value');
      const displayValue = colorEl.getAttribute('data-display-value');
      colors.push({
        id: colorEl.getAttribute('data-color-id'),
        value: colorValue,
        displayValue: displayValue,
      });
      colorEl.replaceWith(` ${nodeRegistry.serialize('color', colorEl, {})} `);
    });

    // Collect remix tags
    clone.querySelectorAll('[data-type="remix"]').forEach((remixEl) => {
      remixEl.replaceWith(` ${nodeRegistry.serialize('remix', remixEl, {})} `);
    });

    // Collect userskill tags
    clone.querySelectorAll('[data-type="userskill"]').forEach((skillEl) => {
      skillEl.replaceWith(` ${nodeRegistry.serialize('skill', skillEl, {})} `);
    });

    // Collect document markers
    clone.querySelectorAll('[data-type="document"]').forEach((docEl) => {
      docEl.replaceWith(nodeRegistry.serialize('document', docEl, {}));
    });

    return {
      text: clone.innerText.trim(),
      images,
      tags,
      colors,
      pendingUploads,
    };
  };

  /**
   * Core sync content to v-model (synchronous, optimized)
   */
  const syncContentCore = (options = {}) => {
    const { forceHeight = false, forceSyncAssets = false } = options || {};
    // Always update isEmpty immediately for instant placeholder feedback
    isEmpty.value = checkEmpty();

    const text = getPlainText();
    // Only emit and do heavy work if content actually changed
    if (text === lastSyncedValue.value) {
      if (forceHeight) adjustHeight?.();
      if (forceSyncAssets) syncAssetsWithEditor?.();
      return;
    }
    // Update last synced value to avoid watch triggering unnecessary updates
    lastSyncedValue.value = text;
    emit?.('update:modelValue', text);
    adjustHeight?.();
    // Sync assets state (remove deleted assets)
    syncAssetsWithEditor?.();
  };

  /**
   * Sync content to v-model (synchronous, no debounce for better responsiveness)
   * @param {Object} options
   * @param {boolean} options.immediate - Kept for API compatibility, ignored now
   */
  const syncContent = (options = {}) => {
    syncContentCore(options);
  };

  /**
   * Parse text with @SeedeMaterial and HEX color values, return segments array by position
   * @param {string} text
   * @returns {Array} Segment array
   */
  const parseTextWithMaterials = (text) => {
    if (!text) return [];

    const segments = [];
    // Match @VeesoMaterial({...}) / @SeedeMaterial({...}) or standalone HEX color #RGB/#RRGGBB
    const materialRegex = /@(?:VeesoMaterial|SeedeMaterial)\((\{[\s\S]*?\})\)/g;
    const documentRegex = /@VeesoDocument\((\{[\s\S]*?\})\)/g;
    const remixRegex = /@VeesoRemix\('([^']+)',\s*'([^']+)'\)/g;
    const userSkillRegex = /@VeesoUserSkill\('([^']+)'\)/g;
    const hexColorRegex = /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})(?=\s|$)/g;

    // Collect all matches and positions
    const matches = [];

    // Collect Material matches
    let match;
    while ((match = materialRegex.exec(text)) !== null) {
      matches.push({
        type: 'material',
        index: match.index,
        length: match[0].length,
        data: match[1],
        raw: match[0],
      });
    }

    // Collect Document matches
    while ((match = documentRegex.exec(text)) !== null) {
      matches.push({
        type: 'document',
        index: match.index,
        length: match[0].length,
        data: match[1],
        raw: match[0],
      });
    }

    // Collect Remix matches
    while ((match = remixRegex.exec(text)) !== null) {
      matches.push({
        type: 'remix',
        index: match.index,
        length: match[0].length,
        data: { id: match[1], name: match[2] },
        raw: match[0],
      });
    }

    // Collect UserSkill matches
    while ((match = userSkillRegex.exec(text)) !== null) {
      matches.push({
        type: 'userskill',
        index: match.index,
        length: match[0].length,
        data: { id: match[1] },
        raw: match[0],
      });
    }

    // Collect HEX color matches (ensure standalone, preceded by whitespace or start)
    while ((match = hexColorRegex.exec(text)) !== null) {
      const charBefore = match.index > 0 ? text[match.index - 1] : '';
      // Ensure color is preceded by whitespace or is at text start
      if (match.index === 0 || /\s/.test(charBefore)) {
        matches.push({
          type: 'color',
          index: match.index,
          length: match[0].length,
          data: `#${match[1]}`,
          raw: match[0],
        });
      }
    }

    // Sort by position
    matches.sort((a, b) => a.index - b.index);

    // Build segments
    let lastIndex = 0;
    for (const m of matches) {
      // Skip overlapping matches
      if (m.index < lastIndex) continue;

      // Add text before
      if (m.index > lastIndex) {
        const textBefore = text.slice(lastIndex, m.index);
        if (textBefore) {
          segments.push({ type: 'text', content: textBefore });
        }
      }

      if (m.type === 'material') {
        try {
          let jsonStr = m.data;
          let braceCount = 0;
          let endIndex = 0;
          for (let i = 0; i < jsonStr.length; i++) {
            if (jsonStr[i] === '{') braceCount++;
            if (jsonStr[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }
          if (endIndex > 0) {
            jsonStr = jsonStr.slice(0, endIndex);
          }

          const materialData = JSON.parse(jsonStr);
          const normalizedUrl = trimWrappingBackticks(materialData.url);
          let resolvedId = materialData.id;
          if (!resolvedId || /^material_/.test(resolvedId)) {
            const match = normalizedUrl.match(/\/asset\/([a-f0-9-]{36})/i);
            if (match) {
              resolvedId = match[1];
            } else if (!resolvedId) {
              resolvedId = `material_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
          }
          const isReference = materialData.type === 'reference';
          const width = materialData.width ?? (isReference ? 120 : undefined);
          const height = materialData.height ?? (isReference ? 120 : undefined);
          const aspectRatio =
            materialData.aspectRatio ??
            (typeof width === 'number' && typeof height === 'number' && height !== 0
              ? Math.round((100 * width) / height) / 100
              : undefined);

          segments.push({
            type: 'material',
            content: {
              id: resolvedId,
              url: normalizedUrl,
              name: materialData.filename || '',
              width,
              height,
              meta: { w: width, h: height },
              aspectRatio,
              tag: materialData.tag || '',
              isMaterial: !isReference,
            },
          });
        } catch {
          segments.push({ type: 'text', content: m.raw });
        }
      } else if (m.type === 'document') {
        try {
          const docData = JSON.parse(m.data);
          segments.push({
            type: 'document',
            content: {
              id: docData.id,
              filename: docData.filename,
            },
          });
        } catch {
          segments.push({ type: 'text', content: m.raw });
        }
      } else if (m.type === 'color') {
        segments.push({
          type: 'color',
          content: {
            id: `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            value: m.data,
            displayValue: normalizeColorToHex(m.data),
          },
        });
      } else if (m.type === 'remix') {
        segments.push({
          type: 'remix',
          content: m.data,
        });
      } else if (m.type === 'userskill') {
        segments.push({
          type: 'userskill',
          content: m.data,
        });
      }

      lastIndex = m.index + m.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remaining = text.slice(lastIndex);
      if (remaining) {
        segments.push({ type: 'text', content: remaining });
      }
    }

    return segments;
  };

  /**
   * Parse and insert text (handles @SeedeMaterial and other special markers) - maintains position relationship
   * @param {string} text
   * @param {Object} options
   * @param {boolean} options.skipSync - Whether to skip final syncContent call
   * @param {Function} options.insertTextAtCursor - Insert text function
   * @param {Function} options.insertImageNode - Insert image node function
   * @param {Function} options.insertDocumentTag - Insert document tag function
   * @param {Function} options.insertColorTagNode - Insert color tag node function
   * @param {Function} options.insertRemixTag - Insert remix tag function
   * @param {Function} options.insertUserSkillTag - Insert user skill tag function
   * @param {Function} options.addAssetToChatState - Add asset to chatState function
   */
  const parseAndInsertText = (text, parseOptions = {}) => {
    const {
      skipSync = false,
      stateBehavior = 'merge',
      insertTextAtCursor,
      insertImageNode,
      insertDocumentTag,
      insertColorTagNode,
      insertRemixTag,
      insertUserSkillTag,
      addAssetToChatState,
    } = parseOptions;

    text = text || '';
    text = text.trim();

    const endsWithNewline = (value) => {
      const s = String(value || '').replace(/[ \t\u00A0]+$/g, '');
      return s.endsWith('\n');
    };

    const startsWithNewline = (value) => {
      const s = String(value || '').replace(/^[ \t\u00A0]+/g, '');
      return s.startsWith('\n');
    };

    // 1. Parse and remove other markers (don't need position maintenance)
    // Parse theme
    const themePayload = parseSeedeTheme(text);
    text = themePayload.content;
    if (themePayload.theme && chatState) {
      chatState.theme = themePayload.theme;
    }

    // Parse canvas size
    const sizePayload = parseVeesoCanvasSize(text);
    text = sizePayload.content;
    if (sizePayload.size) {
      emit?.('update:canvas-size', sizePayload.size);
    }

    // Parse style
    const stylePayload = parseSeedeStyle(text);
    text = stylePayload.content;
    if (stylePayload.style && chatState) {
      chatState.style = stylePayload.style;
    }

    // Parse reference image
    const refImagePayload = parseVeesoReferenceImage(text);
    text = refImagePayload.content;
    const refImages = (refImagePayload.images || [])
      .map((image) => {
        const url = trimWrappingBackticks(image?.url || '');
        if (!url) return null;
        const tag = trimWrappingBackticks(image?.tag || '');
        const id = image?.id || `ref_${hashString(url)}`;
        return {
          ...image,
          id,
          url,
          tag,
          isMaterial: false,
        };
      })
      .filter(Boolean);

    if (chatState) {
      if (stateBehavior === 'replace') {
        if (!chatState.assets) chatState.assets = [];
        chatState.assets = chatState.assets.filter((a) => a?.isMaterial === true);
      }

      if (refImages.length) {
        for (const asset of refImages) {
          addAssetToChatState?.(asset, { checkDuplicate: true });
        }
      }
    }

    // 2. Parse @SeedeMaterial and [[color:value]] to segment array, maintain position relationship
    const segments = parseTextWithMaterials(text);

    // 3. Insert segments in order (images and text separated by newlines, don't duplicate if already separated)
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;
      const nextSegment = i < segments.length - 1 ? segments[i + 1] : null;

      if (segment.type === 'text') {
        // Insert plain text
        insertTextAtCursor?.(segment.content);
      } else if (segment.type === 'material') {
        // Insert material node
        const asset = segment.content;
        // Check if previous text segment already ends with newline, add if not
        if (prevSegment && prevSegment.type === 'text') {
          if (!endsWithNewline(prevSegment.content)) {
            insertTextAtCursor?.('\n');
          }
        }

        // insertImageNode returns false if limit reached
        const inserted = insertImageNode?.(asset);

        if (inserted !== false) {
          // Check next segment: if text and already starts with newline, don't add newline
          const needTrailingNewline =
            !nextSegment ||
            nextSegment.type === 'material' ||
            (nextSegment.type === 'text' && !startsWithNewline(nextSegment.content));

          if (needTrailingNewline) {
            insertTextAtCursor?.('\n');
          }

          addAssetToChatState?.(asset, { checkDuplicate: true });
        }
      } else if (segment.type === 'document') {
        // Insert document tag node
        const docData = segment.content;
        insertDocumentTag?.(docData);
        addAssetToChatState?.(docData, { checkDuplicate: true });
      } else if (segment.type === 'color') {
        // Insert color Tag node
        const colorData = segment.content;
        insertColorTagNode?.(colorData);
      } else if (segment.type === 'remix') {
        insertRemixTag?.(
          { id: segment.content.id, name: segment.content.name },
          {
            skipSync: true,
          }
        );
      } else if (segment.type === 'userskill') {
        insertUserSkillTag?.(
          { id: segment.content.id },
          {
            skipSync: true,
          }
        );
      }
    }

    // Sync content based on skipSync parameter
    if (!skipSync) {
      syncContent({ immediate: true });
    }
  };

  return {
    isEmpty,
    lastSyncedValue,
    checkEmpty,
    getPlainText,
    getFormattedContent,
    syncContent,
    syncContentCore,
    parseTextWithMaterials,
    parseAndInsertText,
  };
}
